import type { BridgeControllers, BridgeSeat as Seat } from '../bridge/seats.js';
import type { Card, Rank } from '../trick/cards.js';
import {
  type RummyGame,
  type RummyPhase,
  type TableMeld,
  canDrawDiscard,
  canDrawStock,
  rummyDiscardable,
  topDiscard,
} from './play.js';

/**
 * Everything one seat may see: its own hand, how many cards the others hold, the melds on the table and the whole face-up discard pile
 * (it is public in Rummy), and how many cards are left in the stock. No other hand and no stock order is ever included.
 */
export interface RummyView {
  readonly viewer: Seat;
  readonly phase: RummyPhase;
  readonly seats: readonly Seat[];
  readonly controllers: BridgeControllers;
  readonly aceHigh: boolean;
  readonly minRank: Rank;
  /** Runs wrap around the ring of ranks. */
  readonly wrap: boolean;
  readonly hand: readonly Card[];
  readonly handSizes: Readonly<Partial<Record<Seat, number>>>;
  readonly melds: readonly TableMeld[];
  /** The face-up pile, the top card last. */
  readonly discard: readonly Card[];
  readonly top: Card | null;
  readonly stockCount: number;
  readonly turn: Seat;
  readonly canAct: boolean;
  readonly canDrawStock: boolean;
  readonly canDrawDiscard: boolean;
  /** The card the viewer took from the pile this turn (it may not go straight back), or null. */
  readonly takenFromDiscard: Card | null;
  readonly discardable: readonly Card[];
  readonly winner: Seat | null;
}

export function rummyViewFor(game: RummyGame, viewer: Seat): RummyView {
  const mine = game.phase !== 'done' && game.turn === viewer;
  return {
    viewer,
    phase: game.phase,
    seats: game.seats,
    controllers: game.controllers,
    aceHigh: game.aceHigh,
    minRank: game.minRank,
    wrap: game.wrap,
    hand: game.hands[viewer] ?? [],
    handSizes: Object.fromEntries(game.seats.map((s) => [s, (game.hands[s] ?? []).length])),
    melds: game.melds,
    discard: game.discard,
    top: topDiscard(game),
    stockCount: game.stock.length,
    turn: game.turn,
    canAct: mine,
    canDrawStock: mine && canDrawStock(game),
    canDrawDiscard: mine && canDrawDiscard(game),
    takenFromDiscard: mine ? game.takenFromDiscard : null,
    discardable: mine ? rummyDiscardable(game) : [],
    winner: game.winner,
  };
}
